import {
    debounce, difference, find, omit,
} from 'lodash';
import { CONSTANTS } from 'services/constants';
import QueryBuilder from 'services/repositories/QueryBuilder';
import * as Q from 'q';
import HttpClient from '../http/HttpClient';

export default class BaseRepository
{
    queue = [];

    constructor()
    {
        this.httpClient = new HttpClient();

        this.all = this.all.bind(this);
        this.buildQueryString = this.buildQueryString.bind(this);
        this._processQueue = debounce(this._processQueue, 100).bind(this);
    }

    // <editor-fold desc="Abstract">

    getEndpoint()
    {
        throw new Error('Method [getEndpoint] must be overwritten by child class');
    }

    // </editor-fold>

    add(data, includes = null)
    {
        return this.request('POST', `${this.getEndpoint()}?${this.buildQueryString({ includes })}`, data);
    }

    all(queryBuilder = null)
    {
        let queryString = '';

        if (queryBuilder)
        {
            queryString = this.buildQueryString(queryBuilder);
        }

        return this.request('GET', `${this.getEndpoint()}?${queryString}`);
    }

    destroy(entityId, includes = null)
    {
        if (!entityId)
        {
            throw new Error('In order to delete a record, an id must be provided');
        }

        return this.request('DELETE', `${this.getEndpoint()}/${entityId}?${this.buildQueryString({ includes })}`);
    }

    edit(data, entityId = null, includes = null)
    {
        let finalId = entityId;
        if (finalId === null)
        {
            const { id } = data;
            finalId = id;
        }

        if (!finalId)
        {
            throw new Error('In order to edit a record, an id must be provided');
        }

        return this.request('PATCH', `${this.getEndpoint()}/${finalId}?${this.buildQueryString({ includes })}`, data);
    }

    findWithQueue(id, includes, ignoreQueue = false)
    {
        if (ignoreQueue)
        {
            return this.find(id, includes);
        }

        return this._addFindToQueue(id, includes);
    }

    find(id, includes = null)
    {
        return this.request('GET', `${this.getEndpoint()}/${id}?${this.buildQueryString({ includes })}`);
    }

    /**
     *
     * @param method
     * @param url
     * @param data
     * @returns {Promise<HttpResponse>}
     */
    request(method, url, data, config = {})
    {
        return this.httpClient.request(method, url, data, config);
    }

    save(data, includes = null)
    {
        if (data.id)
        {
            if (data.id !== CONSTANTS.DATA.NEW_ID)
            {
                return this.edit(data, null, includes);
            }
        }

        return this.add(omit(data, 'id'), includes);
    }

    /**
     * Query
     * @returns {QueryBuilder}
     */
    query()
    {
        return new QueryBuilder(this.all);
    }

    buildQueryString(payload, prefix)
    {
        const str = [];

        Object.keys(payload).forEach((key) =>
        {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = payload[key] === null ? '' : payload[key];

            str.push((v !== null && typeof v === 'object')
                ? this.buildQueryString(v, k)
                : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        });

        return str.join('&');
    }

    _addFindToQueue(id, includes)
    {
        const deferred = Q.defer();

        const key = this.getEndpoint() + id;

        const item = find(this.queue, { key });

        if (item)
        {
            const missingIncludes = difference(includes, item.includes);

            item.includes = [...item.includes, ...missingIncludes];
        }
        else
        {
            this.queue.push({
                deferred,
                key,
                id,
                includes,
            });
        }

        this._processQueue();

        return deferred.promise;
    }

    _processQueue()
    {
        const item = this.queue.shift();

        this.find(item.id, item.includes)
            .then((response) =>
            {
                item.deferred.resolve(response);
            })
            .catch((err) =>
            {
                item.deferred.reject(err);
            });

        if (this.queue.length)
        {
            this._processQueue();
        }
    }
}
